import { useState } from "react";
import { ArrowUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SortEditorPopover } from "./SortEditorPopover";
import type { SortSpec } from "./filterTypes";

interface AddSortButtonProps {
  sorts: SortSpec[];
  onSortsChange: (next: SortSpec[]) => void;
  /** chip-bar — dashed inline pill at the end of the chips bar,
   *  visual sibling of "+ Add filter" (chip-bar variant). Used when
   *  the chips bar is rendering (filters or sorts active).
   *  toolbar — outlined Button next to "+ Add filter" (toolbar
   *  variant) in UnifiedFilterRow. Surfaces Sort as a discoverable
   *  entry point even when zero sorts are active. */
  variant: "chip-bar" | "toolbar";
}

/** Permanent "Sort" entry point — the F4 fix to the F3-bis-frontend
 *  discoverability gap. Mirrors `AddFilterPopover` structurally
 *  (same two variants, same Popover plumbing) but its content is
 *  the existing `SortEditorPopover` rather than a bespoke two-step
 *  picker.
 *
 *  Both variants open the same SortEditorPopover anchored to the
 *  clicked trigger, so an empty `sorts` array opens the editor
 *  in its "no rows + Add sort" empty state, and a non-empty list
 *  opens it with the existing rows ready to edit. */
export function AddSortButton({
  sorts,
  onSortsChange,
  variant,
}: AddSortButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === "chip-bar" ? (
          <button
            type="button"
            aria-label="Edit sort"
            className={cn(
              // Same chip-bar styling as AddFilterPopover so the two
              // affordances are visually paired — F4 deliberately
              // keeps them symmetric (verb-noun consistency was
              // weighed and rejected in spec D1; the noun "Sort"
              // reads cleaner as a button than "Add sort").
              "inline-flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs transition-colors",
              "border-slate-400 bg-white text-slate-600",
              "hover:border-slate-500 hover:bg-slate-50 hover:text-slate-700",
              "dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300",
              "dark:hover:border-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-100",
            )}
          >
            <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />
            Sort
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Edit sort"
            className="text-slate-700"
          >
            <ArrowUpDown className="mr-2 h-3.5 w-3.5" />
            Sort
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={variant === "chip-bar" ? "start" : "end"}
        sideOffset={6}
        className="w-[420px] p-3"
        // F4-bis-bis: see the matching comment in SortChip.tsx.
        // Radix Select renders its listbox in a sibling portal,
        // so without this guard a click on a Select option fires
        // onInteractOutside on this Popover and closes it. The
        // data-radix-popper-content-wrapper attribute is present
        // on every popper-based Radix portal (Select / DropdownMenu
        // / Tooltip / nested Popover), so one closest() check
        // covers them all. Escape + true outside-clicks behave
        // unchanged.
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest("[data-radix-popper-content-wrapper]")) {
            e.preventDefault();
          }
        }}
      >
        <SortEditorPopover sorts={sorts} onChange={onSortsChange} />
      </PopoverContent>
    </Popover>
  );
}
